/**
 * RingtoneFullScreenPlayer — full-screen ringtone player modal.
 *
 * Opens when the user taps the mini-player. Shows:
 *  - Back arrow (closes) + category title (center) + search icon
 *  - Large rounded cover art tile with glow
 *  - Animated neon waveform (teal + purple bars)
 *  - Ringtone title + category badge + duration text
 *  - Player card: progress bar, transport controls, Buy/Owned pill, action circles
 *  - Bottom nav remains visible (player stops above the nav bar)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Download,
  Heart,
  Trash2,
  Music,
  Wallet,
  CheckCircle2,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import { notify } from '@/utils/notifications';
import { useAuth, getIdToken } from '@pooflabs/web';
import { useRingtonePlayerState, useRingtonePlayerActions } from '@/contexts/RingtonePlayerContext';
import { getPoofAPIUrl, getPoofAPIHeaders } from '@/lib/config';
import type { WallpapersResponse } from '@/lib/collections/wallpapers';
import { SkrTokenIcon } from '@/components/SkrTokenIcon';
import { CreatorLabel } from '@/components/CreatorLabel';
import { CreatorAvatar } from '@/components/CreatorAvatar';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Height of the bottom nav pill (matches CategoryPageShell's fixed nav) */
const NAV_HEIGHT = 80; // px — the nav pill + its bottom offset

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatDuration(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds) || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Convert raw SKR price units → display string: "1 SKR", "10 SKR", etc. */
function formatPrice(rawPrice: number): { amount: string; currency: string } {
  const skr = rawPrice / 1_000_000;
  return { amount: skr % 1 === 0 ? String(skr) : skr.toFixed(2), currency: 'SKR' };
}

// ─── Cover art ────────────────────────────────────────────────────────────────

function CoverArt({ ringtone, isPlaying }: { ringtone: WallpapersResponse; isPlaying: boolean }) {
  const hasCover =
    ringtone.imageUrl &&
    !/\.(mp3|wav|ogg|m4a|aac)$/i.test(ringtone.imageUrl.split('?')[0]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 280,
        aspectRatio: '1',
        margin: '0 auto',
        borderRadius: 24,
        overflow: 'hidden',
        boxShadow: isPlaying
          ? '0 0 60px rgba(70,245,224,0.25), 0 0 30px rgba(139,92,246,0.35), 0 20px 60px rgba(0,0,0,0.6)'
          : '0 0 28px rgba(70,245,224,0.08), 0 20px 60px rgba(0,0,0,0.6)',
        transition: 'box-shadow 0.5s ease',
      }}
    >
      {hasCover ? (
        <img
          src={ringtone.imageUrl}
          alt={ringtone.title ?? 'Track'}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <>
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(145deg, #0d0020 0%, #1a0050 35%, #0a001a 60%, #14002e 100%)',
          }} />
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(ellipse 80% 60% at 50% 35%, rgba(70,245,224,0.12) 0%, transparent 65%)',
            animation: isPlaying ? 'coverGlow 3s ease-in-out infinite alternate' : 'none',
          }} />
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(ellipse 60% 70% at 50% 70%, rgba(139,92,246,0.2) 0%, transparent 65%)',
          }} />
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <motion.div
              animate={isPlaying ? { scale: [1, 1.06, 1] } : { scale: 1 }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                width: 96, height: 96, borderRadius: '50%',
                background: 'rgba(70,245,224,0.07)',
                border: '1.5px solid rgba(70,245,224,0.28)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative',
              }}
            >
              <div style={{
                position: 'absolute', inset: -10, borderRadius: '50%',
                border: '1px solid rgba(70,245,224,0.1)',
                animation: isPlaying ? 'pulseRing 2s ease-in-out infinite' : 'none',
              }} />
              <Music size={38} color="#46f5e0" strokeWidth={1.4} />
            </motion.div>
          </div>
          <style>{`
            @keyframes coverGlow {
              0% { opacity: 0.6; }
              100% { opacity: 1; }
            }
            @keyframes pulseRing {
              0% { transform: scale(1); opacity: 0.6; }
              100% { transform: scale(1.15); opacity: 0; }
            }
          `}</style>
        </>
      )}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 80,
        background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)',
        pointerEvents: 'none',
      }} />
    </div>
  );
}

// ─── Progress bar ─────────────────────────────────────────────────────────────

function ProgressBar({
  currentTime,
  duration,
  onSeek,
}: {
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
}) {
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onSeek(ratio * duration);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        onClick={handleClick}
        style={{
          height: 5,
          borderRadius: 99,
          background: 'rgba(255,255,255,0.1)',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: '100%',
          borderRadius: 99,
          background: 'linear-gradient(90deg, #a855f7 0%, #46f5e0 100%)',
          transform: `scaleX(${Math.max(0, Math.min(100, progress)) / 100})`,
          transformOrigin: 'left center',
          transition: 'transform 0.1s linear',
          willChange: 'transform',
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: 'rgba(240,240,245,0.45)', fontFamily: "'DM Mono', monospace" }}>
          {formatTime(currentTime)}
        </span>
        <span style={{ fontSize: 12, color: 'rgba(240,240,245,0.45)', fontFamily: "'DM Mono', monospace" }}>
          {formatTime(duration)}
        </span>
      </div>
    </div>
  );
}

// ─── ActionCircle sub-component ───────────────────────────────────────────────

function ActionCircle({
  icon,
  label,
  onClick,
  color,
  bgColor,
  borderColor,
  disabled = false,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  color: string;
  bgColor: string;
  borderColor: string;
  disabled?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <motion.button
        whileTap={disabled ? {} : { scale: 0.88 }}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        style={{
          width: 52, height: 52, borderRadius: '50%',
          background: bgColor,
          border: `1px solid ${borderColor}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color,
          opacity: disabled ? 0.4 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
        aria-label={label}
        aria-disabled={disabled}
      >
        {icon}
      </motion.button>
      <span style={{
        fontSize: 11,
        color: disabled ? 'rgba(240,240,245,0.25)' : 'rgba(240,240,245,0.45)',
        fontFamily: "'Syne', sans-serif",
        fontWeight: 600,
        letterSpacing: '0.02em',
      }}>
        {label}
      </span>
    </div>
  );
}

// ─── Buy / Owned button ───────────────────────────────────────────────────────

function BuyOwnedButton({
  isPurchased,
  isPurchasing,
  price,
  onBuy,
  buttonRef,
  isGuest = false,
}: {
  isPurchased: boolean;
  isPurchasing: boolean;
  price: number;
  onBuy: () => void;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  isGuest?: boolean;
}) {
  const { amount, currency } = formatPrice(price);

  if (isPurchased) {
    return (
      <div
        style={{
          width: '100%',
          padding: '14px 20px',
          borderRadius: 9999,
          background: 'linear-gradient(135deg, rgba(34,197,94,0.95) 0%, rgba(21,128,61,0.95) 100%)',
          border: '1px solid rgba(74,222,128,0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 9,
          color: '#fff',
          fontSize: 15,
          fontWeight: 700,
          fontFamily: "'Syne', sans-serif",
          letterSpacing: '-0.01em',
          boxShadow: '0 4px 20px rgba(34,197,94,0.3)',
          userSelect: 'none',
        }}
      >
        <CheckCircle2 size={17} strokeWidth={2.2} />
        Owned
      </div>
    );
  }

  if (isPurchasing) {
    return (
      <>
        <style>{`
          @keyframes ringtoneBuySpin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <div
          style={{
            width: '100%',
            padding: '14px 20px',
            borderRadius: 9999,
            background: 'linear-gradient(135deg, rgba(139,92,246,0.9) 0%, rgba(109,40,217,0.9) 100%)',
            border: '1px solid rgba(167,139,250,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            color: '#fff',
            fontSize: 15,
            fontWeight: 700,
            fontFamily: "'Syne', sans-serif",
            letterSpacing: '-0.01em',
            boxShadow: '0 4px 20px rgba(139,92,246,0.3)',
            opacity: 0.68,
            cursor: 'not-allowed',
            userSelect: 'none',
          }}
        >
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              border: '2px solid rgba(255,255,255,0.3)',
              borderTopColor: '#fff',
              animation: 'ringtoneBuySpin 0.8s linear infinite',
            }}
          />
          Purchasing...
        </div>
      </>
    );
  }

  return (
    <motion.button
      ref={buttonRef}
      whileHover={isGuest ? { scale: 1.02 } : { scale: 1.02, boxShadow: '0 0 28px rgba(139,92,246,0.55)' }}
      whileTap={{ scale: 0.97 }}
      onClick={onBuy}
      style={{
        width: '100%',
        padding: '14px 20px',
        borderRadius: 9999,
        background: isGuest
          ? 'rgba(255,255,255,0.1)'
          : 'linear-gradient(135deg, rgba(139,92,246,0.9) 0%, rgba(109,40,217,0.9) 100%)',
        border: isGuest ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(167,139,250,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 9,
        cursor: 'pointer',
        color: '#fff',
        fontSize: 15,
        fontWeight: 700,
        fontFamily: "'Syne', sans-serif",
        letterSpacing: '-0.01em',
        boxShadow: isGuest ? 'none' : '0 4px 20px rgba(139,92,246,0.3)',
      }}
    >
      {isGuest ? <Wallet size={17} strokeWidth={2.2} /> : <SkrTokenIcon size={17} />}
      {isGuest ? 'Connect Wallet' : `Buy for ${amount} ${currency}`}
    </motion.button>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isRingtoneItem(w: WallpapersResponse): boolean {
  const stored = (w as WallpapersResponse & { mediaType?: string }).mediaType;
  if (stored === 'ringtone') return true;
  if (stored && stored !== 'ringtone') return false;
  // Legacy fallback: detect from URL extension
  const url = (w.imageUrl || '').toLowerCase().split('?')[0];
  return /\.(mp3|wav|ogg|m4a|aac)$/.test(url);
}

// ─── Main component ───────────────────────────────────────────────────────────

interface RingtoneFullScreenPlayerProps {
  onToggleFavorite?: (ringtone: WallpapersResponse) => void;
  onPurchase?: (ringtone: WallpapersResponse) => Promise<void>;
  onTrash?: (ringtone: WallpapersResponse) => void | Promise<void>;
  purchasing?: boolean;
  /**
   * Called when the user swipes/taps next or prev and the target item in the
   * playlist is NOT a ringtone (e.g. a wallpaper or live wallpaper). The page
   * should close the ringtone player and open the appropriate visual viewer.
   */
  onAdvanceToVisual?: (item: WallpapersResponse, indexInPlaylist: number) => void;
  /**
   * When true, suppress the enter/exit fade+slide animations so a cross-viewer
   * handoff (ringtone ↔ wallpaper) appears instantaneous with no background flash.
   */
  instantHandoff?: boolean;
}

export function RingtoneFullScreenPlayer({
  onToggleFavorite,
  onPurchase,
  onTrash,
  purchasing = false,
  onAdvanceToVisual,
  instantHandoff = false,
}: RingtoneFullScreenPlayerProps) {
  const {
    currentRingtone,
    isPlaying,
    currentTime,
    duration,
    fullScreenOpen,
    isFavorited,
    isPurchased,
    playlist,
  } = useRingtonePlayerState();

  const {
    pause,
    resume,
    seekTo,
    playNext,
    playPrev,
    closeFullScreen,
    setFavorited,
  } = useRingtonePlayerActions();

  const { user, login } = useAuth();

  // Lock body scroll while full-screen player is open
  useBodyScrollLock(fullScreenOpen);

  // ─── Mixed-playlist navigation helpers ───────────────────────────────────
  // When the playlist contains non-ringtone items (favorites/library mixed view),
  // next/prev may need to hand off to the visual viewer instead of playing audio.

  const handleNext = useCallback(() => {
    if (!currentRingtone || playlist.length === 0) { playNext(); return; }
    const idx = playlist.findIndex(r => r.id === currentRingtone.id);
    const nextIdx = idx >= 0 && idx < playlist.length - 1 ? idx + 1 : 0;
    const next = playlist[nextIdx];
    if (!isRingtoneItem(next) && onAdvanceToVisual) {
      onAdvanceToVisual(next, nextIdx);
    } else {
      playNext();
    }
  }, [currentRingtone, playlist, playNext, onAdvanceToVisual]);

  const handlePrev = useCallback(() => {
    if (!currentRingtone || playlist.length === 0) { playPrev(); return; }
    // If > 3s into track, restart instead of going to previous
    if (currentTime > 3) {
      playPrev(); // context will restart track
      return;
    }
    const idx = playlist.findIndex(r => r.id === currentRingtone.id);
    const prevIdx = idx > 0 ? idx - 1 : playlist.length - 1;
    const prev = playlist[prevIdx];
    if (!isRingtoneItem(prev) && onAdvanceToVisual) {
      onAdvanceToVisual(prev, prevIdx);
    } else {
      playPrev();
    }
  }, [currentRingtone, playlist, currentTime, playPrev, onAdvanceToVisual]);

  // ─── Horizontal swipe → next / prev track ────────────────────────────────
  // Mirrors the WallpaperViewer pattern: only acts on clearly horizontal
  // swipes (>50px and more horizontal than vertical), so vertical scrolling
  // inside the scrollable body is unaffected.
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const handleSwipeStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);
  const handleSwipeEnd = useCallback((e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) handleNext();
      else handlePrev();
    }
  }, [handleNext, handlePrev]);

  // ─── Purchase burst animation ─────────────────────────────────────────────
  const buyButtonRef = useRef<HTMLButtonElement | null>(null);
  const lastBuyOriginRef = useRef<{ x: number; y: number } | null>(null);
  const wasPurchasedRef = useRef(isPurchased);
  const [purchaseBurst, setPurchaseBurst] = useState<{ id: number; x: number; y: number } | null>(null);

  // Pre-compute all random coin params once per burst — prevents Math.random() re-running on every render
  const burstCoins = useMemo(() => {
    if (!purchaseBurst) return [];
    const total = 14;
    return Array.from({ length: total }).map((_, i) => {
      const baseAngle = -180 + (i * 180) / (total - 1);
      const jitter = (Math.random() - 0.5) * 18;
      const angleDeg = baseAngle + jitter;
      const angle = (angleDeg * Math.PI) / 180;
      const distance = 160 + Math.random() * 100;
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance - 120;
      const delay = i * 0.025;
      const size = 22 + Math.random() * 10;
      const rotate = 360 + Math.random() * 180;
      return { dx, dy, delay, size, rotate };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseBurst?.id]);

  // Trigger SKR burst when isPurchased flips false → true
  useEffect(() => {
    if (!wasPurchasedRef.current && isPurchased) {
      const origin = lastBuyOriginRef.current;
      if (origin) {
        setPurchaseBurst({ id: Date.now(), x: origin.x, y: origin.y });
        const t = setTimeout(() => setPurchaseBurst(null), 5400);
        wasPurchasedRef.current = isPurchased;
        return () => clearTimeout(t);
      }
    }
    wasPurchasedRef.current = isPurchased;
  }, [isPurchased]);

  const handlePlayPause = useCallback(() => {
    if (isPlaying) pause();
    else resume();
  }, [isPlaying, pause, resume]);

  const handleToggleFavorite = useCallback(() => {
    if (!currentRingtone) return;
    setFavorited(!isFavorited);
    onToggleFavorite?.(currentRingtone);
  }, [currentRingtone, isFavorited, setFavorited, onToggleFavorite]);

  const handleDownload = useCallback(async () => {
    if (!currentRingtone) return;
    if (!user) {
      // Guest: trigger wallet connection and stay on the same screen
      window.dispatchEvent(new Event('poof:close-overlays'));
      setTimeout(async () => { try { await login(); } catch {} }, 0);
      return;
    }
    if (!isPurchased) {
      notify.error('Purchase this track to download', user.address);
      return;
    }
    try {
      const token = await getIdToken();
      if (!token) { notify.error('Authentication required', user?.address); return; }

      const url = currentRingtone.imageUrl ?? '';
      const ext = /\.(mp3|wav|ogg|m4a|aac)$/i.exec(url.split('?')[0])?.[1] ?? 'mp3';
      const filename = `${currentRingtone.title ?? 'track'}.${ext}`;
      const proxyUrl = getPoofAPIUrl(
        `/api/download-image?wallpaperId=${encodeURIComponent(currentRingtone.id)}&url=${encodeURIComponent(url)}&token=${encodeURIComponent(token)}&wallet=${encodeURIComponent(user.address)}&filename=${encodeURIComponent(filename)}`
      );

      const fileResponse = await fetch(proxyUrl);
      if (!fileResponse.ok) {
        notify.error('Download failed — please try again', user?.address);
        return;
      }
      const blob = await fileResponse.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        document.body.removeChild(a);
      }, 1000);


      try {
        const headers: Record<string, string> = {
          ...getPoofAPIHeaders(),
          'Authorization': `Bearer ${token}`,
          'X-Wallet-Address': user.address,
        };
        fetch(getPoofAPIUrl(`/api/wallpapers/${currentRingtone.id}/download`), {
          method: 'POST', headers,
        }).catch(() => {});
      } catch {}
    } catch {
      notify.error('Download failed — please try again', user?.address);
    }
  }, [currentRingtone, user, login, isPurchased]);

  const handleBuy = useCallback(async () => {
    if (!currentRingtone) return;
    if (!user) {
      // Guest: trigger wallet connection and stay on the same screen
      window.dispatchEvent(new Event('poof:close-overlays'));
      setTimeout(async () => { try { await login(); } catch {} }, 0);
      return;
    }
    // Capture button position so the burst originates from the right spot
    const rect = buyButtonRef.current?.getBoundingClientRect();
    if (rect) {
      lastBuyOriginRef.current = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
    }
    await onPurchase?.(currentRingtone);
  }, [currentRingtone, user, login, onPurchase]);

  const handleTrash = useCallback(async () => {
    if (!currentRingtone) return;
    if (!user) {
      notify.error('Connect your wallet to use trash');
      return;
    }
    await onTrash?.(currentRingtone);
    closeFullScreen();
  }, [currentRingtone, user, onTrash, closeFullScreen]);

  // Tapping the creator's name collapses this full-screen player to the mini
  // player, then navigates to the profile route. Coming back lands on the
  // ringtones list at the track the user started from — the full-screen view is
  // deliberately not reopened.
  const handleBeforeNavigateToProfile = useCallback(() => {
    closeFullScreen();
  }, [closeFullScreen]);

  if (!currentRingtone) return null;

  const category = (currentRingtone as WallpapersResponse & { category?: string }).category ?? '';
  const title = currentRingtone.title ?? 'Untitled';
  const trackPrice = currentRingtone.price ?? 1_000_000;

  return (
    <>
    <AnimatePresence>
      {fullScreenOpen && (
        <motion.div
          key="ringtone-fullscreen"
          initial={instantHandoff ? { opacity: 1, y: 0 } : { opacity: 0, y: '100%' }}
          animate={{ opacity: 1, y: 0 }}
          exit={instantHandoff ? { opacity: 1, y: 0 } : { opacity: 0, y: '100%' }}
          transition={instantHandoff ? { duration: 0 } : { type: 'spring', stiffness: 320, damping: 34, mass: 1 }}
          onTouchStart={handleSwipeStart}
          onTouchEnd={handleSwipeEnd}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            background: '#000',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Soft gradient atmosphere — slowly drifting & pulsing color blobs */}
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
            <motion.div
              style={{
                position: 'absolute',
                top: '12%',
                left: '15%',
                width: 260,
                height: 260,
                borderRadius: '50%',
                background: 'rgba(0,229,204,0.22)',
                filter: 'blur(70px)',
              }}
              animate={{ x: [0, 24, -16, 0], y: [0, -18, 14, 0], scale: [1, 1.12, 0.94, 1] }}
              transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 0 }}
            />
            <motion.div
              style={{
                position: 'absolute',
                top: '40%',
                right: '10%',
                width: 230,
                height: 230,
                borderRadius: '50%',
                background: 'rgba(123,97,255,0.18)',
                filter: 'blur(70px)',
              }}
              animate={{ x: [0, -22, 18, 0], y: [0, 20, -12, 0], scale: [1, 0.92, 1.1, 1] }}
              transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
            />
            <motion.div
              style={{
                position: 'absolute',
                bottom: '18%',
                left: '20%',
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: 'rgba(123,97,255,0.10)',
                filter: 'blur(60px)',
              }}
              animate={{ x: [0, 18, -20, 0], y: [0, -16, 18, 0], scale: [1, 1.08, 0.96, 1] }}
              transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
            />
            <motion.div
              style={{
                position: 'absolute',
                bottom: '8%',
                right: '18%',
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: 'rgba(0,229,204,0.12)',
                filter: 'blur(60px)',
              }}
              animate={{ x: [0, -16, 22, 0], y: [0, 18, -14, 0], scale: [1, 1.1, 0.92, 1] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 4.5 }}
            />
          </div>

          {/* ── Floating back button — no header container, sits over the seamless background ── */}
          <motion.button
            whileHover={{ scale: 1.08, background: 'rgba(255,255,255,0.18)' }}
            whileTap={{ scale: 0.92 }}
            onClick={closeFullScreen}
            style={{
              position: 'absolute',
              top: `calc(env(safe-area-inset-top, 0px) + 20px)`,
              left: 20,
              width: 44, height: 44,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.45)',
              backdropFilter: 'blur(14px)',
              border: '1px solid rgba(255,255,255,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff',
              zIndex: 3,
            }}
            aria-label="Close player"
          >
            <ArrowLeft size={18} />
          </motion.button>

          {/* ── Scrollable body ── */}
          <div
            data-scroll-body
            style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            paddingTop: `calc(env(safe-area-inset-top, 0px) + 76px)`,
            paddingBottom: `calc(${NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 16px)`,
            willChange: 'transform',
            transform: 'translateZ(0)',
          }}>
            {/* Spacer — pushes the title + player card toward the lower half of the screen */}
            <div style={{ padding: '150px 32px 32px' }} />

            {/* Title + category badge + current time */}
            <div style={{ padding: '0 24px 20px', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
              <h1 style={{
                fontSize: 26,
                fontWeight: 900,
                color: '#f0f0f5',
                fontFamily: "'Syne', sans-serif",
                letterSpacing: '-0.03em',
                margin: 0,
                lineHeight: 1.15,
                textAlign: 'center',
                width: '100%',
              }}>
                {title}
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                {/* Category pill — only rendered when category is non-empty */}
                {category ? (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '3px 10px',
                    borderRadius: 99,
                    border: '1px solid rgba(70,245,224,0.45)',
                    color: '#46f5e0',
                    fontSize: 10,
                    fontWeight: 700,
                    fontFamily: "'Syne', sans-serif",
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    background: 'rgba(70,245,224,0.06)',
                  }}>
                    {category}
                  </span>
                ) : null}
              </div>
              {/* Creator attribution — clicking collapses the full-screen player to mini-player, then navigates */}
              {currentRingtone.uploadedBy && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <CreatorAvatar walletAddress={currentRingtone.uploadedBy} size={22} />
                  <CreatorLabel
                    walletAddress={currentRingtone.uploadedBy}
                    style={{ fontSize: 13 }}
                    onBeforeNavigate={handleBeforeNavigateToProfile}
                  />
                </div>
              )}
            </div>

            {/* Player card */}
            <div style={{ padding: '0 16px' }}>
              <div style={{
                background: 'rgba(7,8,15,0.4)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 20,
                padding: '20px 20px 24px',
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
                willChange: 'transform',
                transform: 'translateZ(0)',
                contain: 'layout style paint',
              }}>
                {/* Progress bar */}
                <ProgressBar
                  currentTime={currentTime}
                  duration={duration}
                  onSeek={seekTo}
                />

                {/* Transport controls */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 32,
                }}>
                  <motion.button
                    whileTap={{ scale: 0.88 }}
                    onClick={handlePrev}
                    style={{
                      width: 48, height: 48, borderRadius: '50%',
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', color: 'rgba(240,240,245,0.8)',
                    }}
                    aria-label="Previous"
                  >
                    <SkipBack size={22} strokeWidth={2} />
                  </motion.button>

                  {/* Large play/pause — teal filled circle ~64px */}
                  <motion.button
                    whileTap={{ scale: 0.92 }}
                    onClick={handlePlayPause}
                    style={{
                      width: 64, height: 64, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #46f5e0 0%, #22d3ee 100%)',
                      border: 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: isPlaying
                        ? '0 0 28px rgba(70,245,224,0.6), 0 8px 24px rgba(0,0,0,0.4)'
                        : '0 0 16px rgba(70,245,224,0.25), 0 8px 24px rgba(0,0,0,0.4)',
                      transition: 'box-shadow 0.3s ease',
                    }}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPlaying
                      ? <Pause size={26} color="#08070f" fill="#08070f" />
                      : <Play size={26} color="#08070f" fill="#08070f" style={{ marginLeft: 3 }} />}
                  </motion.button>

                  <motion.button
                    whileTap={{ scale: 0.88 }}
                    onClick={handleNext}
                    style={{
                      width: 48, height: 48, borderRadius: '50%',
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', color: 'rgba(240,240,245,0.8)',
                    }}
                    aria-label="Next"
                  >
                    <SkipForward size={22} strokeWidth={2} />
                  </motion.button>
                </div>

                {/* Buy / Owned pill button */}
                <BuyOwnedButton
                  isPurchased={isPurchased}
                  isPurchasing={purchasing}
                  price={trackPrice}
                  onBuy={handleBuy}
                  buttonRef={buyButtonRef}
                  isGuest={!user}
                />

                {/* Three action circles */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 36,
                }}>
                  <ActionCircle
                    icon={<Download size={20} strokeWidth={2} />}
                    label="Download"
                    onClick={handleDownload}
                    color="rgba(240,240,245,0.8)"
                    bgColor="rgba(255,255,255,0.06)"
                    borderColor="rgba(255,255,255,0.1)"
                    disabled={!isPurchased}
                  />

                  <ActionCircle
                    icon={
                      <Heart
                        size={20}
                        strokeWidth={2}
                        fill={isFavorited ? 'hsl(262, 83%, 66%)' : 'none'}
                        color={isFavorited ? 'hsl(262, 83%, 66%)' : 'rgba(240,240,245,0.8)'}
                      />
                    }
                    label="Favorite"
                    onClick={handleToggleFavorite}
                    color={isFavorited ? 'hsl(262, 83%, 66%)' : 'rgba(240,240,245,0.8)'}
                    bgColor={isFavorited ? 'hsla(262, 83%, 66%, 0.15)' : 'rgba(255,255,255,0.06)'}
                    borderColor={isFavorited ? 'hsla(262, 83%, 66%, 0.4)' : 'rgba(255,255,255,0.1)'}
                  />

                  <ActionCircle
                    icon={<Trash2 size={20} strokeWidth={2} />}
                    label="Trash"
                    onClick={handleTrash}
                    color="rgba(240,240,245,0.8)"
                    bgColor="rgba(255,255,255,0.06)"
                    borderColor="rgba(255,255,255,0.1)"
                    disabled={!isPurchased}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* SKR token burst — celebratory animation when a ringtone purchase completes */}
          <AnimatePresence>
            {purchaseBurst && (
              <motion.div
                key={`skr-burst-ringtone-${purchaseBurst.id}`}
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                  position: 'fixed',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 10100,
                }}
              >
                {burstCoins.map(({ dx, dy, delay, size, rotate }, i) => (
                  <motion.div
                    key={i}
                    initial={{
                      x: purchaseBurst.x,
                      y: purchaseBurst.y,
                      scale: 0.4,
                      opacity: 0,
                      rotate: 0,
                    }}
                    animate={{
                      x: purchaseBurst.x + dx,
                      y: purchaseBurst.y + dy,
                      scale: [0.4, 1.1, 0.9],
                      opacity: [0, 1, 1, 0],
                      rotate,
                    }}
                    transition={{
                      duration: 6.1,
                      delay,
                      ease: [0.16, 1, 0.3, 1],
                      times: [0, 0.05, 0.85, 1],
                    }}
                    style={{
                      position: 'absolute',
                      left: -size / 2,
                      top: -size / 2,
                      width: size,
                      height: size,
                      willChange: 'transform, opacity',
                    }}
                  >
                    <SkrTokenIcon size={size} />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
}

export default RingtoneFullScreenPlayer;
